var _a;
// Math short-cuts for often used methods and values
const { abs } = Math;
const { sqrt } = Math;
// eslint-disable-next-line no-restricted-properties
const log2 = (_a = Math.log2) !== null && _a !== void 0 ? _a : (x => Math.log(x) * Math.LOG2E);
function clamp(value, min, max) {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}
function getDiscriminant(aParam, bParam, cParam) {
    // d = b^2 - a * c  computed accurately enough by a tricky scheme.
    // Ported from @hkrish's polysolve.c
    function split(value) {
        const x = value * 134217729;
        const y = value - x;
        const hi = y + x; // Don't optimize y away!
        const lo = value - hi;
        return [hi, lo];
    }
    let Det = bParam * bParam - aParam * cParam;
    const Eigen = bParam * bParam + aParam * cParam;
    if (abs(Det) * 3 < Eigen) {
        const ad = split(aParam);
        const bd = split(bParam);
        const cd = split(cParam);
        const pPara = bParam * bParam;
        const dp = bd[0] * bd[0] - pPara + 2 * bd[0] * bd[1] + bd[1] * bd[1];
        const qPara = aParam * cParam;
        const dq = ad[0] * cd[0] -
            qPara +
            ad[0] * cd[1] +
            ad[1] * cd[0] +
            ad[1] * cd[1];
        Det = pPara - qPara + (dp - dq); // Don’t omit parentheses!
    }
    return Det;
}
function getNormalizationFactor(...values) {
    // Normalize coefficients à la Jenkins & Traub's RPOLY.
    // Normalization is done by scaling coefficients with a power of 2, so
    // that all the bits in the mantissa remain unchanged.
    // Use the infinity norm (max(sum(abs(a)…)) to determine the appropriate
    // scale factor. See @hkrish in #1087#issuecomment-231526156
    const norm = Math.max(...values);
    return norm && (norm < 1e-8 || norm > 1e8)
        ? Math.pow(2, -Math.round(log2(norm)))
        : 0;
}
/**
 * A very small absolute value used to check if a value is very close to
 * zero. The value should be large enough to offset any floating point
 * noise, but small enough to be meaningful in computation in a nominal
 * range (see MACHINE_EPSILON).
 *
 * http://docs.oracle.com/cd/E19957-01/806-3568/ncg_goldberg.html
 * http://www.cs.berkeley.edu/~wkahan/Math128/Cubic.pdf
 */
/**
 * The machine epsilon for a double precision (Javascript Number) is
 * 2.220446049250313e-16. (try this in the js console:
 *     (function(){ for (var e = 1; 1 < 1+e/2;) e/=2; return e }())
 *
 * The constant MACHINE_EPSILON here refers to the constants δ and ε
 * such that, the error introduced by addition, multiplication on a
 * 64bit float (js Number) will be less than δ and ε. That is to say,
 * for all X and Y representable by a js Number object, S and P be their
 * 'exact' sum and product respectively, then
 * |S - (x+y)| <= δ|S|, and |P - (x*y)| <= ε|P|.
 * This amounts to about half of the actual machine epsilon.
 */
// MACHINE_EPSILON= MACHINE_EPSILON,
export const MACHINE_EPSILON = 1.12e-16;
export const EPSILON = 1e-12;
/**
 * Like EPSILON but larger
 */
export const EPS = 0.01;
/**
 * The epsilon to be used when handling curve-time parameters. This
 * cannot be smaller, because errors add up to around 2e-7 in the bezier
 * fat-line clipping code as a result of recursive sub-division.
 */
export const CURVETIME_EPSILON = 1e-8;
/**
 * The epsilon to be used when performing "geometric" checks, such as
 * distances between points and lines.
 */
export const GEOMETRIC_EPSILON = 1e-7;
/**
 * The epsilon to be used when performing "trigonometric" checks, such
 * as examining cross products to check for collinearity.
 */
export const TRIGONOMETRIC_EPSILON = 1e-8;
/**
 * Kappa is the value which which to scale the curve handles when
 * drawing a circle with bezier curves.
 *
 * http://whizkidtech.redprince.net/bezier/circle/kappa/
 */
export const KAPPA = (4 * (sqrt(2) - 1)) / 3;
/**
 * Checks if the value is 0, within a tolerance defined by
 * Numerical.EPSILON.
 */
export function isZero(val) {
    return val >= -EPSILON && val <= EPSILON;
}
export function isMachineZero(val) {
    return val >= -MACHINE_EPSILON && val <= MACHINE_EPSILON;
}
/**
 * Solve a quadratic equation in a numerically robust manner;
 * given a quadratic equation ax² + bx + c = 0, find the values of x.
 *
 * References:
 *  Kahan W. - "To Solve a Real Cubic Equation"
 *  http://www.cs.berkeley.edu/~wkahan/Math128/Cubic.pdf
 *  Blinn J. - "How to solve a Quadratic Equation"
 *  Harikrishnan G.
 *  https://gist.github.com/hkrish/9e0de1f121971ee0fbab281f5c986de9
 *
 * @param {Number} aParam the quadratic term
 * @param {Number} bParam the linear term
 * @param {Number} cParam the constant term
 * @param {Number[]} roots the array to store the roots in
 * @param {Number} [min] the lower bound of the allowed roots
 * @param {Number} [max] the upper bound of the allowed roots
 * @return {Number} the number of real roots found, or -1 if there are
 * infinite solutions
 *
 * @author Harikrishnan Gopalakrishnan <hari.exeption@gmail.com>
 */
function solveQuadratic(aParam, bParam, cParam, roots, min, max) {
    let x1;
    let x2 = Infinity;
    if (abs(aParam) < EPSILON) {
        // This could just be a linear equation
        if (abs(bParam) < EPSILON) {
            return abs(cParam) < EPSILON ? -1 : 0;
        }
        x1 = -cParam / bParam;
    }
    else {
        // a, b, c are expected to be the coefficients of the equation:
        // Ax² - 2Bx + C == 0, so we take b = -b/2:
        bParam *= -0.5;
        let Dv = getDiscriminant(aParam, bParam, cParam);
        // If the discriminant is very small, we can try to normalize
        // the coefficients, so that we may get better accuracy.
        if (Dv && abs(Dv) < MACHINE_EPSILON) {
            const normFactor = getNormalizationFactor(abs(aParam), abs(bParam), abs(cParam));
            if (normFactor) {
                aParam *= normFactor;
                bParam *= normFactor;
                cParam *= normFactor;
                Dv = getDiscriminant(aParam, bParam, cParam);
            }
        }
        if (Dv >= -MACHINE_EPSILON) {
            // No real roots if D < 0
            const Qpara = Dv < 0 ? 0 : sqrt(Dv);
            const Rpara = bParam + (bParam < 0 ? -Qpara : Qpara);
            // Try to minimize floating point noise.
            if (Rpara === 0) {
                x1 = cParam / aParam;
                x2 = -x1;
            }
            else {
                x1 = Rpara / aParam;
                x2 = cParam / Rpara;
            }
        }
    }
    let count = 0;
    const boundless = min == null;
    const minB = min - EPSILON;
    const maxB = max + EPSILON;
    // We need to include EPSILON in the comparisons with min / max,
    // as some solutions are ever so lightly out of bounds.
    if (Number.isFinite(x1) && (boundless || (x1 > minB && x1 < maxB))) {
        roots[count] = boundless ? x1 : clamp(x1, min, max);
        count += 1;
    }
    if (x2 !== x1 &&
        Number.isFinite(x2) &&
        (boundless || (x2 > minB && x2 < maxB))) {
        roots[count] = boundless ? x2 : clamp(x2, min, max);
        count += 1;
    }
    return count;
}
/**
 * Solve a cubic equation, using numerically stable methods,
 * given an equation of the form ax³ + bx² + cx + d = 0.
 *
 * This algorithm avoids the trigonometric/inverse trigonometric
 * calculations required by the "Italins"' formula. Cardano's method
 * works well enough for exact computations, this method takes a
 * numerical approach where the double precision error bound is kept
 * very low.
 *
 * References:
 *  Kahan W. - "To Solve a Real Cubic Equation"
 *   http://www.cs.berkeley.edu/~wkahan/Math128/Cubic.pdf
 *  Harikrishnan G.
 *  https://gist.github.com/hkrish/9e0de1f121971ee0fbab281f5c986de9
 *
 * W. Kahan's paper contains inferences on accuracy of cubic
 * zero-finding methods. Also testing methods for robustness.
 *
 * @param {Number} aParam the cubic term (x³ term)
 * @param {Number} bParam the quadratic term (x² term)
 * @param {Number} cParam the linear term (x term)
 * @param {Number} dParam the constant term
 * @param {Number[]} roots the array to store the roots in
 * @param {Number} [min] the lower bound of the allowed roots
 * @param {Number} [max] the upper bound of the allowed roots
 * @return {Number} the number of real roots found, or -1 if there are
 * infinite solutions
 *
 * @author Harikrishnan Gopalakrishnan <hari.exeption@gmail.com>
 */
export function solveCubic(aParam, bParam, cParam, dParam, roots, min, max) {
    const normFactor = getNormalizationFactor(abs(aParam), abs(bParam), abs(cParam), abs(dParam));
    let x;
    let b1;
    let c2;
    let qd;
    let qv;
    if (normFactor) {
        aParam *= normFactor;
        bParam *= normFactor;
        cParam *= normFactor;
        dParam *= normFactor;
    }
    const evaluate = (x0) => {
        x = x0;
        // Evaluate q, q', b1 and c2 at x
        const tmp = aParam * x;
        b1 = tmp + bParam;
        c2 = b1 * x + cParam;
        qd = (tmp + b1) * x + c2;
        qv = c2 * x + dParam;
    };
    // If a or d is zero, we only need to solve a quadratic, so we set
    // the coefficients appropriately.
    if (abs(aParam) < EPSILON) {
        aParam = bParam;
        b1 = cParam;
        c2 = dParam;
        x = Infinity;
    }
    else if (abs(dParam) < EPSILON) {
        b1 = bParam;
        c2 = cParam;
        x = 0;
    }
    else {
        // Here onwards we iterate for the leftmost root. Proceed to
        // deflate the cubic into a quadratic (as a side effect to the
        // iteration) and solve the quadratic.
        evaluate(-(bParam / aParam) / 3);
        // Get a good initial approximation.
        const t = qv / aParam;
        const rCoef = Math.pow(abs(t), (1 / 3));
        const sCoef = t < 0 ? -1 : 1;
        const td = -qd / aParam;
        // See Kahan's notes on why 1.324718*... works.
        const rd = td > 0 ? 1.324717957244746 * Math.max(rCoef, sqrt(td)) : rCoef;
        let x0 = x - sCoef * rd;
        if (x0 !== x) {
            do {
                evaluate(x0);
                // Newton's. Divide by 1 + MACHINE_EPSILON (1.000...002)
                // to avoid x0 crossing over a root.
                x0 = qd === 0 ? x : x - qv / qd / (1 + MACHINE_EPSILON);
            } while (sCoef * x0 > sCoef * x);
            // Adjust the coefficients for the quadratic.
            if (abs(aParam) * x * x > abs(dParam / x)) {
                c2 = -dParam / x;
                b1 = (c2 - cParam) / x;
            }
        }
    }
    // The cubic has been deflated to a quadratic.
    let count = solveQuadratic(aParam, b1, c2, roots, min, max);
    const boundless = min == null;
    if (Number.isFinite(x) &&
        (count === 0 || (count > 0 && x !== roots[0] && x !== roots[1])) &&
        (boundless || (x > min - EPSILON && x < max + EPSILON))) {
        roots[count] = boundless ? x : clamp(x, min, max);
        count += 1;
    }
    return count;
}
